// src/components/ResultsSection.jsx
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStoredBonusDisplay } from '../../utils/bonusConfig';
import { getMatchTitleLabel, getMatchTitleObjects } from '../../utils/matchTitles';
import { isShowInLiveWindow } from '../../utils/showLiveWindow';
import '../../styles/ShowEditor.css';

const normalizeGender = value => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['uomo', 'male', 'm', 'man'].includes(normalized)) return 'male';
  if (['donna', 'female', 'f', 'woman'].includes(normalized)) return 'female';
  return '';
};

const isDisqualificationResult = match =>
  Boolean(match?.isDisqualificationWin || match?.notes?.includes('Vittoria per squalifica'));

const getChampionshipList = championshipsList =>
  Array.isArray(championshipsList)
    ? championshipsList.filter(Boolean)
    : championshipsList
      ? [championshipsList]
      : [];

const getMixedChampionshipImages = championshipsList =>
  getChampionshipList(championshipsList).flatMap(championship => {
    if (championship?.type !== 'mixed_tag_team') return [];

    return [
      championship.imageMale
        ? {
            src: championship.imageMale,
            alt: `${championship.name} Uomo`
          }
        : null,
      championship.imageFemale
        ? {
            src: championship.imageFemale,
            alt: `${championship.name} Donna`
          }
        : null
    ].filter(Boolean);
  });

const getChampionshipImagesForParticipant = (championshipsList, participant) =>
  getChampionshipList(championshipsList).reduce((images, championship) => {
    if (championship.type !== 'mixed_tag_team') {
      if (championship.image) {
        images.push({ src: championship.image, alt: championship.name });
      }
      return images;
    }

    const normalizedGender = normalizeGender(participant?.gender);

    if (normalizedGender === 'male' && championship.imageMale) {
      images.push({
        src: championship.imageMale,
        alt: `${championship.name} Uomo`
      });
    } else if (normalizedGender === 'female' && championship.imageFemale) {
      images.push({
        src: championship.imageFemale,
        alt: `${championship.name} Donna`
      });
    } else {
      const fallbackImage =
        championship.image || championship.imageMale || championship.imageFemale;

      if (fallbackImage) {
        images.push({ src: fallbackImage, alt: championship.name });
      }
    }

    return images;
  }, []);

const hasUnknownMixedChampionshipGender = (championship, participant) =>
  championship?.type === 'mixed_tag_team' && !normalizeGender(participant?.gender);

const getTitleRecipientIds = (match, items, winners) => {
  if (!match?.title) return new Set();

  if (isDisqualificationResult(match)) {
    return new Set(
      items
        .filter(item => !winners.has(item.wrestlerId))
        .map(item => item.wrestlerId)
    );
  }

  return new Set(winners);
};

const shouldUseCenterMixedTitleImages = (match, championshipsList, items, winners) => {
  const mixedChampionships = getChampionshipList(championshipsList).filter(
    championship => championship.type === 'mixed_tag_team'
  );
  if (mixedChampionships.length === 0) return false;

  const recipientIds = getTitleRecipientIds(match, items, winners);
  return mixedChampionships.some(championship =>
    items.some(item =>
      recipientIds.has(item.wrestlerId) &&
      hasUnknownMixedChampionshipGender(championship, item)
    )
  );
};

const getCenterSeparatorIndex = (match, items) => {
  if (!Array.isArray(items) || items.length < 2) return -1;

  const teams = Array.isArray(match?.teams) ? match.teams : [];
  if (teams.length >= 2) {
    const boundaryIndexes = [];

    items.forEach((item, index) => {
      const nextItem = items[index + 1];
      if (!nextItem) return;

      const currentTeamIndex = teams.findIndex(team =>
        (team.participants || []).includes(item.wrestlerId)
      );
      const nextTeamIndex = teams.findIndex(team =>
        (team.participants || []).includes(nextItem.wrestlerId)
      );

      if (
        currentTeamIndex !== -1 &&
        nextTeamIndex !== -1 &&
        currentTeamIndex !== nextTeamIndex
      ) {
        boundaryIndexes.push(index);
      }
    });

    if (boundaryIndexes.length > 0) {
      return boundaryIndexes[Math.floor(boundaryIndexes.length / 2)];
    }
  }

  return Math.floor((items.length - 2) / 2);
};

const ChampionshipImages = React.memo(({ images, isMobile, placement = 'card' }) => {
  const handleChampionshipError = useCallback((e) => {
    e.target.onerror = null;
    e.target.style.display = 'none';
  }, []);

  if (!images || images.length === 0) return null;

  return (
    <div className={`results-title-image-stack results-title-image-stack--${placement} ${isMobile ? 'results-title-image-stack--mobile' : ''}`}>
      {images.map((image, index) => (
        <img
          key={`${image.src}-${index}`}
          src={image.src}
          alt={image.alt}
          onError={handleChampionshipError}
        />
      ))}
    </div>
  );
});

const VsSeparator = React.memo(({ isMobile, championshipsList = [], showCenterTitle, dimmed = false }) => {
  const centerTitleImages = showCenterTitle ? getMixedChampionshipImages(championshipsList) : [];
  const hasCenterTitleImages = centerTitleImages.length > 0;

  return (
    <div
      className={`${isMobile ? 'mobile-vs-separator' : 'vs-separator-desktop'} results-vs-separator ${hasCenterTitleImages ? 'results-vs-separator--with-title' : ''}`}
      style={{ flex: '0 0 auto', zIndex: 1 }}
    >
      {hasCenterTitleImages && (
        <ChampionshipImages
          images={centerTitleImages}
          isMobile={isMobile}
          placement="separator"
        />
      )}
      <span
        className={`badge text-white ${isMobile ? 'mobile-vs-badge' : 'px-3 py-2'}`}
        style={{
          backgroundColor: 'var(--brand-primary)',
          opacity: dimmed ? 0.7 : undefined,
          ...(!isMobile && { fontSize: '0.8rem' })
        }}
      >
        VS
      </span>
    </div>
  );
});

const ANNOUNCED_CARD_WIDTH_MOBILE = 'clamp(110px, 32vw, 140px)';
const ANNOUNCED_CARD_HEIGHT_MOBILE = 'clamp(140px, 38vw, 170px)';
const ANNOUNCED_CARD_WIDTH_DESKTOP = '150px';
const ANNOUNCED_CARD_HEIGHT_DESKTOP = '160px';

const AnnouncedWrestlerCard = React.memo(({ p, displayName, onClick, isMobile, getTitleIcon, championships, isHighlighted, factionNames = [] }) => {
  const handleImageError = useCallback((e) => {
    e.target.onerror = null;
    e.target.src = '/placeholder-wrestler.jpg';
  }, []);

  const titleIcon = !p.isGuest && p.title ? getTitleIcon?.(p.title) : null;
  const titleName = titleIcon
    ? championships?.find?.(championship => championship.value === p.title)?.name
    : null;

  return (
    <div
      className="wrestler-card shadow-sm position-relative overflow-hidden rounded-4 cursor-pointer"
      style={{
        width: isMobile ? ANNOUNCED_CARD_WIDTH_MOBILE : ANNOUNCED_CARD_WIDTH_DESKTOP,
        height: isMobile ? ANNOUNCED_CARD_HEIGHT_MOBILE : ANNOUNCED_CARD_HEIGHT_DESKTOP,
        flex: '0 0 auto',
        background: isHighlighted ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.04)',
        border: isHighlighted
          ? '1px solid rgba(194, 157, 84, 0.38)'
          : '1px solid rgba(146, 201, 252, 0.14)',
        boxShadow: '0 10px 22px rgba(0, 0, 0, 0.24)'
      }}
      onClick={onClick}
    >
      <div className="position-relative" style={{ height: '100%' }}>
        <img
          src={p.wrestlerImage || '/placeholder-wrestler.jpg'}
          alt={displayName}
          className="img-fluid w-100 h-100 bg-dark"
          style={{
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'top center'
          }}
          onError={handleImageError}
          loading="lazy"
        />
        {titleIcon && (
          <div className="position-absolute top-0 start-0 m-1">
            <span
              className="badge p-1"
              style={{ backgroundColor: 'var(--brand-secondary)', color: 'white' }}
              title={titleName || 'Titolo'}
            >
              {titleIcon}
            </span>
          </div>
        )}
        <div
          className="position-absolute bottom-0 start-0 end-0 p-2 name-overlay"
          style={{ background: 'linear-gradient(to top, rgba(18, 24, 31), rgba(0, 0, 0, 0))' }}
        >
          <h6 className="text-white text-truncate mb-0 fw-semibold">{displayName}</h6>
          {isHighlighted && factionNames.length > 0 && (
            <small className="d-block text-truncate" style={{ color: '#f1d39a' }}>
              {factionNames.join(', ')}
            </small>
          )}
        </div>
      </div>
    </div>
  );
});

const WrestlerCard = React.memo(({ p, wrestlerNames, isWinner, scoreColor, onClick, isMobile, championshipsList = [], shouldShowTitleOnLoser, match, getTitleIcon, showPoints = true, suppressTitleImage = false, variant = 'compact', championships, isHighlighted = false, factionNames = [] }) => {
  const handleImageError = useCallback((e) => {
    e.target.onerror = null;
    e.target.src = '/placeholder-wrestler.jpg';
  }, []);

  // Logica semplificata - controlla direttamente le note del match
  const isDisqualificationWin = isDisqualificationResult(match);
  const displayName = p.name || wrestlerNames[p.wrestlerId] || p.wrestlerName || 'Wrestler Sconosciuto';

  if (variant === 'announced') {
    return (
      <AnnouncedWrestlerCard
        p={p}
        displayName={displayName}
        onClick={onClick}
        isMobile={isMobile}
        getTitleIcon={getTitleIcon}
        championships={championships}
        isHighlighted={!p.isGuest && isHighlighted}
        factionNames={factionNames}
      />
    );
  }

  const championshipImages = getChampionshipImagesForParticipant(championshipsList, p);
  const showTitleImage =
    !suppressTitleImage &&
    championshipImages.length > 0 &&
    (isDisqualificationWin ? !isWinner : isWinner);

  return (
    <div
      className={`card shadow-sm border-0 overflow-hidden results-wrestler-card ${isWinner ? 'results-wrestler-card-winner' : ''} ${isMobile ? '' : 'h-100'}`}
      style={{
        cursor: 'pointer',
        width: isMobile ? 'clamp(80px, 20vw, 100px)' : '100%',
        height: isMobile ? 'clamp(100px, 25vw, 120px)' : 'auto'
      }}
      onClick={onClick}
    >
      <div className="position-relative w-100" style={{ height: isMobile ? '100%' : '180px' }}>
        <img
          src={p.wrestlerImage || '/placeholder-wrestler.jpg'}
          alt={displayName}
          className="img-fluid w-100 h-100"
          style={{
            objectFit: 'cover',
            objectPosition: 'top center'
          }}
          onError={handleImageError}
          loading="lazy"
        />

        {/* Titolo sul wrestler che mantiene o conquista la cintura */}
        {showTitleImage && (
          <div className="position-absolute bottom-0 start-50 translate-middle"
            style={{
              zIndex: 12,
              transform: 'translate(-50%, -70%)',
              display: 'flex',
              alignItems: 'flex-end',
              gap: isMobile ? '0.2rem' : '0.35rem',
              ...(isMobile && {
                bottom: '15px',
                transform: 'translate(-50%, 0)'
              })
            }}>
            <ChampionshipImages
              images={championshipImages}
              isMobile={isMobile}
              placement="card"
            />
          </div>
        )}

        {/* Badge punti o nome se guest */}
        <div className={`position-absolute bottom-0 start-50 translate-middle-x ${isMobile ? 'mb-1' : 'mb-2 w-100 px-2'}`}>
          {!showPoints ? (
            <span
              className={`badge bg-dark text-white ${isMobile ? 'fs-6 mobile-badge' : 'fs-6 shadow-sm w-100'}`}
              style={{
                padding: !isMobile ? '6px 8px' : undefined,
                fontSize: isMobile
                  ? Math.max(10, Math.min(14, 120 / (displayName.length || 1))) + 'px'
                  : '0.9rem',
                maxWidth: isMobile ? '90px' : '100%',
                display: 'inline-block',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={displayName}
            >
              {displayName}
            </span>
          ) : p.isGuest ? (
            <span
              className={`badge bg-dark text-white ${isMobile ? 'fs-6 mobile-badge' : 'fs-6 shadow-sm w-100'}`}
              style={{
                padding: !isMobile ? '6px 8px' : undefined,
                fontSize: isMobile
                  ? Math.max(10, Math.min(14, 120 / (displayName.length || 1))) + 'px' // scala in base alla lunghezza
                  : '0.9rem',
                maxWidth: isMobile ? '90px' : '100%',
                display: 'inline-block',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={displayName} // se il testo viene tagliato, si vede tutto al hover
            >
              {displayName}
            </span>


          ) : (
<span 
    className={`badge results-score-badge results-score-badge-${isWinner ? 'winner' : scoreColor} ${isMobile ? 'fs-6 mobile-badge' : 'fs-6 shadow-sm w-100'}`}
    style={!isMobile ? { padding: '6px 8px', fontSize: '0.9rem' } : {}}
  >
    {p.totalPoints >= 0 ? '+' : ''}{p.totalPoints}
  </span>
          )}
        </div>

      </div>
    </div>
  );
});

const ResultsSection = ({ participations, matches, wrestlerNames, bonusMap, championships, getTitleIcon, shouldShowTitleOnLoser, showData, mode = 'results', futureViewMode, onFutureViewModeChange, userFactionWrestlersMap = {} }) => {
  const navigate = useNavigate();
  // Imposta sempre 'table' como view mode di default
  const [viewMode, setViewMode] = useState('table');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const matchContainerRefs = useRef(new Map());
  const observerRef = useRef(null);
  const isAnnounced = mode === 'announced';
  const showPoints = !isAnnounced;
  const isShowLive = useMemo(
    () => !isAnnounced && isShowInLiveWindow(showData, currentTime),
    [showData, currentTime, isAnnounced]
  );

  // Memoized calculations
  const getWinners = useCallback((match) => {
    const winners = new Set();
    if (!match.winner) return winners;

    if (match.isMultiTeam && match.teams) {
      const winningTeam = match.teams.find(t => `team${t.id}` === match.winner);
      if (winningTeam) {
        winningTeam.participants.forEach(id => winners.add(id));
      }
    } else if (match.winner.startsWith('team') && match.teams) {
      const winningTeam = match.teams.find(t => `team${t.id}` === match.winner);
      if (winningTeam) {
        winningTeam.participants.forEach(id => winners.add(id));
      }
    } else {
      winners.add(match.winner);
    }
    return winners;
  }, []);

  const getMatchTitle = useCallback((match) => {
    const titleLabel = getMatchTitleLabel(match, championships);
    const championship = titleLabel
      ? {
        name: titleLabel
          .split(' + ')
          .map((label, index, collection) =>
            index === collection.length - 1
              ? label
              : label.replace(/Champion$/, 'Championship')
          )
          .join(' + ')
      }
      : null;

    if (titleLabel) {
      return `${championship?.name || match.title}ship${match.stipulation && match.stipulation !== 'Normal' ? ` • ${match.stipulation} Match` : ''
        }`;
    }

    return match.stipulation && match.stipulation !== 'Normal' ?
      `${match.stipulation} Match` : 'Match';
  }, [championships]);

  const groupedParticipations = useMemo(() => {
    // Mappa degli wrestler standard
    const participationsMap = {};
    participations.forEach(p => {
      participationsMap[p.wrestlerId] = p;
    });

    // Funzione helper per ottenere dati di un partecipante o guest
    const getParticipantData = (id, match) => {
      // Se esiste come partecipante standard
      if (participationsMap[id]) return participationsMap[id];

      // Se è un guest
      const guest = match.guests?.find(g => g.id === id);
      if (guest) {
        return {
          wrestlerId: guest.id,
          wrestlerImage: guest.imageUrl,
          name: guest.name,
          totalPoints: 0, // o calcola punti se serve
          notes: guest.notes || null,
          isGuest: true,
        };
      }

      // Fallback: ID sconosciuto
      return {
        wrestlerId: id,
        wrestlerImage: '/placeholder-wrestler.jpg',
        name: id,
        totalPoints: 0,
        notes: null,
      };
    };

    const groups = [];

    // ORDINA I MATCH per timestamp dal più vecchio al più recente
    const sortedMatches = [...matches].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

    sortedMatches.forEach(m => {
      const items = (m.participants || []).map(id => getParticipantData(id, m));
      if (items.length > 0) groups.push({ type: 'match', match: m, items });
    });

    const matchedIds = new Set(groups.flatMap(g => g.items.map(i => i.wrestlerId)));
    const unmatched = participations.filter(p => !matchedIds.has(p.wrestlerId));
    if (unmatched.length > 0) groups.push({ type: 'unmatched', items: unmatched });

    return groups;
  }, [participations, matches]);


  const sortedParticipations = useMemo(() => {
    return [...participations].sort((a, b) => {
      if (a.totalPoints === b.totalPoints) {
        return (wrestlerNames[a.wrestlerId] || '').localeCompare(wrestlerNames[b.wrestlerId] || '');
      }
      return sortOrder === 'asc' ? a.totalPoints - b.totalPoints : b.totalPoints - a.totalPoints;
    });
  }, [participations, wrestlerNames, sortOrder]);

  const announcedParticipations = useMemo(() => {
    return [...participations].sort((a, b) => {
      const nameA = a.wrestlerName || a.name || wrestlerNames[a.wrestlerId] || '';
      const nameB = b.wrestlerName || b.name || wrestlerNames[b.wrestlerId] || '';
      return nameA.localeCompare(nameB);
    });
  }, [participations, wrestlerNames]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, []);

  // Optimized observer setup
  useEffect(() => {
    const checkAll = () => {
      matchContainerRefs.current.forEach((el) => {
        if (!el) return;
        try {
          const fits = el.scrollWidth <= el.clientWidth + 1;
          if (fits) el.classList.add('center-when-fit');
          else el.classList.remove('center-when-fit');
        } catch (e) { /* ignore */ }
      });
    };

    // Use debounced resize handler
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkAll, 100);
    };

    // Setup observer only if we have elements to observe
    if (matchContainerRefs.current.size > 0) {
      observerRef.current = new MutationObserver(checkAll);
      matchContainerRefs.current.forEach(el => {
        // Non osservare 'attributes' qui: checkAll aggiunge/rimuove la classe
        // 'center-when-fit' sullo stesso elemento osservato, e se la larghezza
        // e' al limite tra "entra"/"non entra" questo crea un loop infinito
        // add/remove -> mutation -> checkAll -> add/remove che blocca la pagina.
        if (el) observerRef.current.observe(el, {
          childList: true,
          subtree: true
        });
      });

      checkAll();
      window.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [groupedParticipations]); // Only depend on groupedParticipations

  // Event handlers
  const handleSortToggle = useCallback(() => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  }, []);

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  const handleWrestlerClick = useCallback((wrestlerId) => {
    navigate(`/wrestlers/${wrestlerId}`);
  }, [navigate]);

  const renderLiveAnnouncedCards = useCallback((isMobile) => {
    return (
      <div className={`results-section-content results-announced-grid ${isMobile ? 'results-announced-grid--mobile' : ''}`}>
        {announcedParticipations.map((p, idx) => (
          <div
            key={`live-announced-${p.wrestlerId}-${idx}`}
            className="results-announced-grid__item"
          >
            <WrestlerCard
              p={{
                ...p,
                name: p.wrestlerName || p.name || wrestlerNames[p.wrestlerId],
              }}
              wrestlerNames={wrestlerNames}
              isWinner={false}
              scoreColor="secondary"
              onClick={!p.isGuest ? () => handleWrestlerClick(p.wrestlerId) : () => setSelectedGuest(p)}
              isMobile={isMobile}
              shouldShowTitleOnLoser={shouldShowTitleOnLoser}
              match={null}
              getTitleIcon={getTitleIcon}
              showPoints={false}
            />
          </div>
        ))}
      </div>
    );
  }, [announcedParticipations, wrestlerNames, handleWrestlerClick, shouldShowTitleOnLoser, getTitleIcon]);

  // Render mobile cards
  const renderMobileCards = useCallback(() => {
    return (
      <div className="container-fluid px-0 results-section-content">
        {groupedParticipations.map((g, gi) => {
          const winners = g.type === 'match' ? getWinners(g.match) : new Set();
          const matchTitle = g.type === 'match' ? getMatchTitle(g.match) : null;
          const matchChampionships = g.type === 'match'
            ? getMatchTitleObjects(g.match, championships)
            : [];
          const showCenterMixedTitle = g.type === 'match'
            ? shouldUseCenterMixedTitleImages(g.match, matchChampionships, g.items, winners)
            : false;
          const centerSeparatorIndex = showCenterMixedTitle
            ? getCenterSeparatorIndex(g.match, g.items)
            : -1;

          return (
            <div key={`group-${gi}`} className="mb-3">
              {gi > 0 && <hr className="my-1 opacity-25" />}

              {g.type === 'match' && (
                <div className="text-center mb-2 py-1 results-match-header">
                  <strong className="responsive-header">{matchTitle}</strong>
                  {/* Note del match senza icona */}
                  {g.match.notes && (
                    <div className="mt-1">
                      <small className="text-muted-brand">
                        {g.match.notes}
                      </small>
                    </div>
                  )}
                </div>
              )}

              {g.type === 'unmatched' && (
                <div className="text-center mb-2">
                  <strong className="responsive-header">Fuori Match</strong>
                </div>
              )}

              <div
                ref={(el) => {
                  if (g.type === 'match') matchContainerRefs.current.set(`m-${gi}`, el);
                  else matchContainerRefs.current.delete(`m-${gi}`);
                }}
                className={g.type === 'match' ?
                  'position-relative overflow-auto px-2 match-items-container' :
                  'd-flex flex-wrap align-items-center justify-content-center position-relative match-items-container'
                }
                style={g.type === 'match' ? { textAlign: 'center' } : {}}
              >
                {g.type === 'match' ? (
                  <div className="inline-items" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                    {g.items.map((p, idx) => {
                      const scoreColor = p.totalPoints > 0 ? 'success' : p.totalPoints < 0 ? 'danger' : 'secondary';
                      const isWinner = winners.has(p.wrestlerId);
                      const nextWrestler = g.items[idx + 1];
                      const showCenterTitle = showCenterMixedTitle && idx === centerSeparatorIndex;

                      let vsEl = null;
                      if (g.match && g.match.isMultiTeam && g.match.teams) {
                        const currentTeam = (g.match.teams || []).find(t => (t.participants || []).includes(p.wrestlerId));
                        const nextTeam = (g.match.teams || []).find(t => (t.participants || []).includes(nextWrestler?.wrestlerId));
                        if (currentTeam && nextTeam && currentTeam.id !== nextTeam.id) {
                          vsEl = (
                            <VsSeparator
                              isMobile={true}
                              championshipsList={matchChampionships}
                              showCenterTitle={showCenterTitle}
                            />
                          );
                        }
                      } else if (g.match && g.match.teams && g.match.teams.length === 2) {
                        const teamA = g.match.teams.find(t => t.id === 1);
                        const lastOfTeamA = teamA && teamA.participants ? teamA.participants[teamA.participants.length - 1] : null;
                        if (p.wrestlerId === lastOfTeamA && idx < g.items.length - 1) {
                          vsEl = (
                            <VsSeparator
                              isMobile={true}
                              championshipsList={matchChampionships}
                              showCenterTitle={showCenterTitle}
                              dimmed={true}
                            />
                          );
                        }
                      } else {
                        if (idx < g.items.length - 1) {
                          vsEl = (
                            <VsSeparator
                              isMobile={true}
                              championshipsList={matchChampionships}
                              showCenterTitle={showCenterTitle}
                            />
                          );
                        }
                      }

                      const mobileFactionMeta = userFactionWrestlersMap[p.wrestlerId];

                      return (
                        <React.Fragment key={`${gi}-${p.wrestlerId}-${idx}`}>
                          <div className="mobile-wrestler-card" style={{ flex: '0 0 auto', zIndex: 1, marginRight: '0.5rem' }}>
                            <WrestlerCard
                              p={p}
                              wrestlerNames={{ ...wrestlerNames, [p.wrestlerId]: p.name }}
                              isWinner={isWinner}
                              scoreColor={scoreColor}
                              onClick={!p.isGuest ? () => handleWrestlerClick(p.wrestlerId) : () => setSelectedGuest(p)}
                              isMobile={true}
                              championshipsList={matchChampionships}
                              shouldShowTitleOnLoser={shouldShowTitleOnLoser}
                              match={g.match}
                              getTitleIcon={getTitleIcon}
                              suppressTitleImage={showCenterMixedTitle}
                              showPoints={showPoints}
                              variant={isAnnounced ? 'announced' : 'compact'}
                              championships={championships}
                              isHighlighted={Boolean(mobileFactionMeta)}
                              factionNames={mobileFactionMeta?.factionNames || []}
                            />
                          </div>
                          {vsEl}
                        </React.Fragment>
                      );
                    })}
                  </div>
                ) : (
                  <div className="d-flex flex-wrap align-items-center justify-content-center position-relative">
                    {g.items.map((p, idx) => {
                      const scoreColor = p.totalPoints > 0 ? 'success' : p.totalPoints < 0 ? 'danger' : 'secondary';
                      const mobileUnmatchedFactionMeta = userFactionWrestlersMap[p.wrestlerId];
                      return (
                        <React.Fragment key={`${gi}-${p.wrestlerId}-${idx}`}>
                          <div className="mobile-wrestler-card" style={{ marginRight: '0.5rem' }}>
                            <WrestlerCard
                              p={p}
                              wrestlerNames={{ ...wrestlerNames, [p.wrestlerId]: p.name }}
                              isWinner={false}
                              scoreColor={scoreColor}
                              onClick={!p.isGuest ? () => handleWrestlerClick(p.wrestlerId) : () => setSelectedGuest(p)}
                              isMobile={true}
                              shouldShowTitleOnLoser={shouldShowTitleOnLoser}
                              match={null}
                              getTitleIcon={getTitleIcon}
                              showPoints={showPoints}
                              variant={isAnnounced ? 'announced' : 'compact'}
                              championships={championships}
                              isHighlighted={Boolean(mobileUnmatchedFactionMeta)}
                              factionNames={mobileUnmatchedFactionMeta?.factionNames || []}
                            />
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [groupedParticipations, getWinners, getMatchTitle, championships, wrestlerNames, handleWrestlerClick, shouldShowTitleOnLoser, getTitleIcon, showPoints, isAnnounced, userFactionWrestlersMap]);

  // Render desktop cards
  const renderDesktopCards = useCallback(() => {
    return (
      <div className="p-0 results-section-content">
        {groupedParticipations.map((g, gi) => {
          const winners = g.type === 'match' ? getWinners(g.match) : new Set();
          const matchTitle = g.type === 'match' ? getMatchTitle(g.match) : null;
          const matchChampionships = g.type === 'match'
            ? getMatchTitleObjects(g.match, championships)
            : [];
          const showCenterMixedTitle = g.type === 'match'
            ? shouldUseCenterMixedTitleImages(g.match, matchChampionships, g.items, winners)
            : false;
          const centerSeparatorIndex = showCenterMixedTitle
            ? getCenterSeparatorIndex(g.match, g.items)
            : -1;

          // Calcola se serve lo scroll basandosi sul numero di squadre, non di wrestler
          const teamCount = g.match?.teams?.length || 1;
          const estimatedWidth = teamCount * 300; // Stima più larga per le squadre
          const needsHorizontalScroll = estimatedWidth > 800;

          return (
            <div key={`dgroup-${gi}`} className="mb-4">
              {gi > 0 && <hr className="my-2 opacity-25" />}
              {g.type === 'match' && (
                <div className="text-center mb-3 py-2 rounded results-match-header">
                  <h6 className="mb-0 fw-bold text-light fs-5">{matchTitle}</h6>
                  {/* Note del match senza icona */}
                  {g.match.notes && (
                    <div className="mt-1">
                      <small className="text-muted-brand">
                        {g.match.notes}
                      </small>
                    </div>
                  )}
                </div>
              )}

              {g.type === 'unmatched' && (
                <div className="text-center mb-3">
                  <h6 className="mb-0 fw-bold text-light fs-5">Wrestler Fuori Match</h6>
                </div>
              )}

              <div
                className={`position-relative ${needsHorizontalScroll ? 'desktop-scroll-container' : 'd-flex flex-wrap justify-content-center gap-3'}`}
                style={needsHorizontalScroll ? {
                  display: 'flex',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  padding: '16px 8px',
                  scrollbarWidth: 'thin',
                  gap: '16px',
                  justifyContent: 'flex-start'
                } : {}}
              >
                {g.type === 'match' && g.match.isMultiTeam ? (
                  // RENDER PER MATCH A SQUADRE
                  <>
                    {g.match.teams.map((team, teamIndex) => {
                      const teamWrestlers = g.items.filter(p =>
                        team.participants.includes(p.wrestlerId)
                      );

                      return (
                        <React.Fragment key={`team-${team.id}`}>
                          {/* Container della squadra */}
                          <div className="team-container d-flex flex-column align-items-center">

                            {/* Wrestler della squadra */}
                            <div className="d-flex flex-wrap justify-content-center gap-2">
                              {teamWrestlers.map((p, wrestlerIndex) => {
                                const scoreColor = p.totalPoints > 0 ? 'success' : p.totalPoints < 0 ? 'danger' : 'secondary';
                                const isWinner = winners.has(p.wrestlerId);
                                const teamFactionMeta = userFactionWrestlersMap[p.wrestlerId];
                                return (
                                  <div
                                    key={`team-${team.id}-wrestler-${p.wrestlerId}`}
                                    className="desktop-wrestler-card position-relative"
                                    style={{
                                      flex: '0 0 auto',
                                      width: '150px'
                                    }}
                                  >
                                    <WrestlerCard
                                      p={p}
                                      wrestlerNames={{ ...wrestlerNames, [p.wrestlerId]: p.name }}
                                      isWinner={isWinner}
                                      scoreColor={scoreColor}
                                      onClick={!p.isGuest ? () => handleWrestlerClick(p.wrestlerId) : () => setSelectedGuest(p)}
                                      isMobile={false}
                                      championshipsList={matchChampionships}
                                      shouldShowTitleOnLoser={shouldShowTitleOnLoser}
                                      match={g.match}
                                      getTitleIcon={getTitleIcon}
                                      suppressTitleImage={showCenterMixedTitle}
                                      showPoints={showPoints}
                                      variant={isAnnounced ? 'announced' : 'compact'}
                                      championships={championships}
                                      isHighlighted={Boolean(teamFactionMeta)}
                                      factionNames={teamFactionMeta?.factionNames || []}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Badge VS tra le squadre (non dopo l'ultima squadra) */}
                          {teamIndex < g.match.teams.length - 1 && (
                            <VsSeparator
                              isMobile={false}
                              championshipsList={matchChampionships}
                              showCenterTitle={showCenterMixedTitle && teamIndex === Math.floor((g.match.teams.length - 2) / 2)}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </>
                ) : (
                  // RENDER PER MATCH SINGOLI (codice originale)
                  <>
                    {g.items.map((p, idx) => {
                      const scoreColor = p.totalPoints > 0 ? 'success' : p.totalPoints < 0 ? 'danger' : 'secondary';
                      const isWinner = winners.has(p.wrestlerId);
                      const singleFactionMeta = userFactionWrestlersMap[p.wrestlerId];
                      return (
                        <React.Fragment key={`d-${gi}-${p.wrestlerId}-${idx}`}>
                          <div
                            className="desktop-wrestler-card position-relative"
                            style={{
                              flex: '0 0 auto',
                              width: isAnnounced ? '150px' : (needsHorizontalScroll ? '140px' : '150px')
                            }}
                          >
                            <WrestlerCard
                              p={p}
                              wrestlerNames={{ ...wrestlerNames, [p.wrestlerId]: p.name }}
                              isWinner={isWinner}
                              scoreColor={scoreColor}
                              onClick={!p.isGuest ? () => handleWrestlerClick(p.wrestlerId) : () => setSelectedGuest(p)}
                              isMobile={false}
                              championshipsList={matchChampionships}
                              shouldShowTitleOnLoser={shouldShowTitleOnLoser}
                              match={g.match}
                              getTitleIcon={getTitleIcon}
                              suppressTitleImage={showCenterMixedTitle}
                              showPoints={showPoints}
                              variant={isAnnounced ? 'announced' : 'compact'}
                              championships={championships}
                              isHighlighted={Boolean(singleFactionMeta)}
                              factionNames={singleFactionMeta?.factionNames || []}
                            />
                          </div>

                          {g.type === 'match' && idx < g.items.length - 1 && (
                            <VsSeparator
                              isMobile={false}
                              championshipsList={matchChampionships}
                              showCenterTitle={showCenterMixedTitle && idx === centerSeparatorIndex}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [groupedParticipations, getWinners, getMatchTitle, championships, wrestlerNames, handleWrestlerClick, shouldShowTitleOnLoser, getTitleIcon, showPoints, isAnnounced, userFactionWrestlersMap]);

  // Render table view
  const renderTableView = useCallback(() => {
    return (
      <div className={`results-table-live-wrapper ${isShowLive ? 'is-live' : ''}`}>
        <div className="table-responsive results-table-show-wrapper">
          <table className="table table-hover table-dark align-middle mb-0 results-table-show">
            <thead>
              <tr>
                <th className="ps-4">Wrestler</th>
                <th>Bonus/Malus</th>
                <th className="text-end pe-4">Punti</th>
              </tr>
            </thead>
            <tbody>
              {sortedParticipations.map((p, idx) => {
                const bonusItems = (p.bonuses || []).map((val, bonusIndex) => {
                  const normalizedVal = String(val).toLowerCase();
                  return getStoredBonusDisplay(
                    val,
                    (baseValue) => bonusMap[baseValue.toLowerCase()] || bonusMap[normalizedVal],
                    showData,
                    p.bonusPoints?.[bonusIndex]
                  );
                });

                return (
                  <tr
                    key={idx}
                    className="hover-bg clickable-row results-table-show-row"
                    onClick={() => handleWrestlerClick(p.wrestlerId)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="ps-4 fw-semibold">
                      <div>
                        {wrestlerNames[p.wrestlerId] || p.wrestlerId}
                      </div>
                    </td>
                    <td>
                      <div className="bonus-badges">
                        {bonusItems.length > 0 ? (
                          bonusItems.map((b, i) => {
                            let badgeClass = 'neutral';
                            if (b.points > 0) {
                              badgeClass = 'positive';
                            } else if (b.points < 0) {
                              badgeClass = 'negative';
                            }

                            return (
                              <span
                                key={i}
                                className={`bonus-badge ${badgeClass} ${b.isExtVariant ? 'bonus-badge--ext' : ''}`}
                                title={b.isExtVariant ? `${b.label} (Extra)` : b.label}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/bonus/${encodeURIComponent(String(b.baseValue).toLowerCase())}`);
                                }}
                              >
                                <span className="bonus-badge__label">{b.label}</span>
                                {b.isExtVariant && <span className="bonus-badge__variant">Extra</span>}
                                <span className="bonus-badge__points">{b.points > 0 ? '+' : ''}{b.points}</span>
                              </span>
                            );
                          })
                        ) : (
                          <span className="text-muted-brand small">-</span>
                        )}
                      </div>
                    </td>
                    <td className={`text-end pe-4 fw-bold ${p.totalPoints > 0 ? 'text-success' : p.totalPoints < 0 ? 'text-danger' : 'text-muted-brand'}`}>
                      {p.totalPoints}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {isShowLive && (
          <div className="results-live-overlay" aria-live="polite">
            <div className="results-live-overlay__panel">
              <span className="results-live-overlay__dot"></span>
              <div>
                <div className="results-live-overlay__title">SHOW IN LIVE</div>
                <div className="results-live-overlay__subtitle">I risultati saranno visibili alle ore 12:00</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, [sortedParticipations, bonusMap, wrestlerNames, handleWrestlerClick, navigate, isShowLive, showData]);

  if (participations.length === 0) {
    return (
      <div className="card shadow-lg border-0 rounded-3 overflow-hidden mb-4 results-shell show-results-shell">
        <div className="card-header py-3 results-header show-results-header">
          <h5 className="mb-0 fs-5 d-flex align-items-center">
            <i className={`bi ${isAnnounced ? 'bi-diagram-3' : 'bi-people'} me-2`}></i> {isAnnounced ? 'Match Annunciati' : 'Risultati'}
            {isShowLive && (
              <span className="badge results-live-badge ms-2">SHOW IN LIVE</span>
            )}
          </h5>
        </div>
        <div className="card-body results-empty-body">
          <div className="text-center py-5">
            <i className="bi bi-inbox fs-1 text-muted-brand" />
            <p className="text-muted-brand mt-3">{isAnnounced ? 'Nessun match annunciato' : 'Nessuna partecipazione registrata'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow-lg border-0 rounded-3 overflow-hidden mb-4 results-shell show-results-shell">
      <div className="card-header py-3 results-header show-results-header">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0 fs-5 d-flex align-items-center">
            <i className={`bi ${isAnnounced ? 'bi-diagram-3' : 'bi-people'} me-2`}></i> {isAnnounced ? 'Match Annunciati' : 'Risultati'}
          </h5>
          {isAnnounced ? (
            <div className="btn-group btn-group-sm results-view-toggle" role="group" aria-label="View mode">
              <button
                type="button"
                className={`btn ${futureViewMode === 'list' ? 'results-view-btn-active' : 'results-view-btn'}`}
                onClick={() => onFutureViewModeChange && onFutureViewModeChange('list')}
                title="Lista"
              >
                <i className="bi bi-people-fill"></i>
              </button>
              <button
                type="button"
                className={`btn ${futureViewMode === 'match' ? 'results-view-btn-active' : 'results-view-btn'}`}
                onClick={() => onFutureViewModeChange && onFutureViewModeChange('match')}
                title="Match"
              >
                <i className="bi bi-diagram-3"></i>
              </button>
            </div>
          ) : (
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn py-1 px-3 d-flex align-items-center results-toolbar-btn"
                onClick={handleSortToggle}
                title="Ordina"
              >
                <i className={`bi bi-sort-${sortOrder === 'asc' ? 'down' : 'up'}-alt me-1`} /> Punti
              </button>
              <div className="btn-group btn-group-sm results-view-toggle" role="group" aria-label="View mode">
                <button
                  type="button"
                  className={`btn ${viewMode === 'table' ? 'results-view-btn-active' : 'results-view-btn'}`}
                  onClick={() => handleViewModeChange('table')}
                  title="Tabella"
                >
                  <i className="bi bi-table"></i>
                </button>
                <button
                  type="button"
                  className={`btn ${viewMode === 'cards' ? 'results-view-btn-active' : 'results-view-btn'}`}
                  onClick={() => handleViewModeChange('cards')}
                  title="Card"
                >
                  <i className="bi bi-grid-3x3-gap-fill"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card-body p-0 results-body">
        {isAnnounced ? (
          <>
            <div className="d-md-none">{renderMobileCards()}</div>
            <div className="d-none d-md-block">{renderDesktopCards()}</div>
          </>
        ) : (
          <>
            {/* Mobile View */}
            <div className="d-md-none">
              {viewMode === 'cards'
                ? (isShowLive ? renderLiveAnnouncedCards(true) : renderMobileCards())
                : renderTableView()}
            </div>

            {/* Desktop View */}
            <div className="d-none d-md-block">
              {viewMode === 'cards'
                ? (isShowLive ? renderLiveAnnouncedCards(false) : renderDesktopCards())
                : renderTableView()}
            </div>
          </>
        )}
      </div>

      {/* Guest Modal */}
      {selectedGuest && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => setSelectedGuest(null)}
          ></div>
          <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content border-0 bg-transparent shadow-none">
                {/* Bottone chiusura rosso CORRETTO */}
                <button
                  type="button"
                  className="position-absolute bg-transparent border-0 p-0"
                  style={{
                    top: '0px',
                    right: '20px',
                    zIndex: 10,
                    width: '30px',
                    height: '30px',
                    color: '#dc3545',
                    fontSize: '40px',
                    lineHeight: '0.5',
                    cursor: 'pointer',
                    opacity: 0.9,
                    transition: 'opacity 0.2s'
                  }}
                  onClick={() => setSelectedGuest(null)}
                  onMouseEnter={(e) => e.target.style.opacity = '1'}
                  onMouseLeave={(e) => e.target.style.opacity = '0.9'}
                  aria-label="Close"
                >
                  &times;
                </button>

                {/* Container con margini laterali */}
                <div className="mx-3 mx-md-4 mx-lg-5">
                  {/* Immagine responsive con margini */}
                  <div className="position-relative rounded-3 overflow-hidden shadow-lg">
                    <img
                      src={selectedGuest.wrestlerImage || '/placeholder-wrestler.jpg'}
                      alt={selectedGuest.name}
                      className="img-fluid w-100"
                      style={{
                        maxHeight: '75vh',
                        minHeight: '300px',
                        objectFit: 'contain',
                        objectPosition: 'center top',
                        backgroundColor: '#f8f9fa',
                        display: 'block'
                      }}
                      onError={(e) => {
                        e.target.src = '/placeholder-wrestler.jpg';
                      }}
                    />

                    {/* Nome con sfondo ombreggiato */}
                    <div className="position-absolute bottom-0 start-0 w-100 p-3 p-md-4"
                      style={{
                        background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)'
                      }}>
                      <h3 className="text-white mb-0 text-center fw-bold"
                        style={{
                          textShadow: '1px 1px 4px rgba(0,0,0,0.7)',
                          fontSize: 'clamp(1.8rem, 4.5vw, 3rem)',
                          letterSpacing: '0.5px'
                        }}>
                        {selectedGuest.name}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ResultsSection;
